import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import axios from "axios";

// Components
import Header from "./components/Header";
import Hero from "./components/Hero";
import Properties from "./components/Properties";
import About from "./components/About";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import CMS from "./components/CMS/CMS";
import Login from "./components/Login";
import CookiesBanner from "./components/CookiesBanner";
import VoiceCallButton from "./components/VoiceCallButton";
import ContractSignature from "./components/ContractSignature";
import PasswordReset from "./components/PasswordReset";
import { TermsAndConditions, PrivacyPolicy, CookiePolicy } from "./components/LegalPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

// Content Context
export const ContentContext = React.createContext();

// Home Page Component
const HomePage = () => {
  return (
    <div className="App">
      <Header />
      <Hero />
      <About />
      <Properties />
      <Contact />
      <Footer />
      <CookiesBanner />
      <VoiceCallButton />
    </div>
  );
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      await verifyToken();
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Error al iniciar sesión');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Content Provider Component
const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
    loadMenu();
  }, []);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`);
      setMenu(response.data);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const updateContent = async (type, newContent) => {
    try {
      await axios.put(`${API}/cms/content/${type}`, {
        content: newContent
      });
      setContent(prev => ({ ...prev, [type]: newContent }));
    } catch (error) {
      console.error('Error updating content:', error);
      throw error;
    }
  };

  const updateMenu = async (newMenu) => {
    try {
      await axios.put(`${API}/cms/menu`, {
        items: newMenu
      });
      setMenu(newMenu);
    } catch (error) {
      console.error('Error updating menu:', error);
      throw error;
    }
  };

  return (
    <ContentContext.Provider value={{ 
      content, 
      menu, 
      loading, 
      updateContent, 
      updateMenu, 
      loadContent, 
      loadMenu 
    }}>
      {children}
    </ContentContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return user && user.is_admin ? children : <Navigate to="/login" />;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <ContentProvider>
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/terminos" element={<TermsAndConditions />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/contracts/:contractId/sign" element={<ContractSignature />} />
            <Route 
              path="/cms/*" 
              element={
                <ProtectedRoute>
                  <CMS />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute>
                  <CMS />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </Router>
      </ContentProvider>
    </AuthProvider>
  );
}

export default App;